// Footprint reducers for the Polaris slice: the stale-read guard and the epoch
// bookkeeping around mutations. Split out of polarisSlice to keep that file
// within the repo's per-file size budget.
import {ActionReducerMapBuilder, isAnyOf} from "@reduxjs/toolkit";
import {TFootprintResponse} from "api/polarisApi/types";
import type {TPolarisReducerState} from "store/polaris/types";
import {
	addClusterFromText,
	loadFootprint,
	refreshFootprint,
	removeCluster,
	removeClusterCompany,
	retryFootprint,
	TFootprintMutationMeta,
	TFootprintReadMeta,
	updateFootprintScope,
} from "./footprintThunks";

// Applies a footprint GET (loadFootprint / refreshFootprint) unless it is
// stale. Mutation responses never go through here — they always apply.
const applyFootprintRead = (
	state: TPolarisReducerState,
	payload: TFootprintResponse,
	meta: Partial<TFootprintReadMeta>,
) => {
	// A GET for a search the user has since left.
	if (meta.searchId !== undefined && meta.searchId !== state.search_id) return;

	// With nothing stored yet there is nothing newer to protect.
	if (state.footprint !== null) {
		// A mutation started or applied after this GET left.
		if (meta.footprintEpoch !== undefined && meta.footprintEpoch !== state.footprint_epoch) {
			return;
		}

		// A GET that left after this one has already landed.
		if (
			meta.footprintReadSeq !== undefined &&
			meta.footprintReadSeq <= state.footprint_read_seq
		) {
			return;
		}
	}

	state.footprint = payload;

	if (meta.footprintReadSeq !== undefined) {
		state.footprint_read_seq = Math.max(state.footprint_read_seq, meta.footprintReadSeq);
	}
};

// Matchers only, so the slice can register them after all its addCase calls.
export const addFootprintMatchers = (builder: ActionReducerMapBuilder<TPolarisReducerState>) => {
	builder
		// Every footprint read AND mutation (scope patch, cluster/company
		// removal, retry, add cluster) returns the full footprint state. Reads
		// go through the stale-read guard. refreshFootprint has no
		// pending/rejected handling on purpose: it must stay silent (see the
		// slice's rejected matcher).
		.addMatcher(
			isAnyOf(loadFootprint.fulfilled, refreshFootprint.fulfilled),
			(state, action) => {
				applyFootprintRead(state, action.payload, action.meta);
			},
		)
		// Mutations bump the epoch when they leave (GETs already in flight may
		// predate them) and again when they apply (GETs that left meanwhile may
		// predate the server-side write). A rejected mutation changed nothing,
		// so GETs overlapping it stay valid. The response itself always applies
		// on its own search.
		.addMatcher(
			isAnyOf(
				updateFootprintScope.pending,
				removeCluster.pending,
				removeClusterCompany.pending,
				retryFootprint.pending,
				addClusterFromText.pending,
			),
			(state) => {
				state.footprint_epoch += 1;
			},
		)
		.addMatcher(
			isAnyOf(
				updateFootprintScope.fulfilled,
				removeCluster.fulfilled,
				removeClusterCompany.fulfilled,
				retryFootprint.fulfilled,
				addClusterFromText.fulfilled,
			),
			(state, action) => {
				// A result for a search the user has since left (e.g. a retry
				// still in flight on navigation): applying it would show that
				// search's map on the new one. Drop it, epoch included.
				const {searchId} = action.meta as Partial<TFootprintMutationMeta>;

				if (searchId !== undefined && searchId !== state.search_id) return;

				state.footprint = action.payload;
				state.footprint_epoch += 1;
			},
		);
};
